import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Renderer2,
  SimpleChanges,
  ViewEncapsulation,
  HostListener,
  Output,
  EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';

import { MarqueeService } from './services/marquee.service';
import { QueueService } from './services/queue.service';
import { KeyMappingService } from './services/key-mapping.service';
import { RemoteButton, RemoteControlState } from './remote-control.model';
import { ResponsiveFontDirective } from './responsive-font.directive';
@Component({
  selector: 'app-remote-control',
  templateUrl: './remote-control.component.html',
  styleUrls: ['./remote-control.component.scss'],
  // Necessary for the styles that are generated dynamically in the Marquee
  encapsulation: ViewEncapsulation.None,
})
export class RemoteControlComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() terminal_vid: string;
  @Input() terminal_connected = true;
  @Input() class = 'p-4';
  @Output() buttonQueue = new EventEmitter<RemoteButton[]>();
  @Output() lastSendedButton = new EventEmitter<RemoteButton | null>();

  //  @ViewChild('lcdScreen') lcdScreen?: ElementRef<HTMLElement>;
  @ViewChild('lcdText') lcdText?: ElementRef<HTMLElement>;
  //@ViewChild('statusScreen') statusScreen?: ElementRef<HTMLElement>;
  @ViewChild('statusText') statusText?: ElementRef<HTMLElement>;

  public mobileButtonQueue: RemoteButton[];
  public mobileLastSendedButton: RemoteButton | null;
  public buttons: RemoteButton[] = Array.from({ length: 16 }, (_, i) => ({
    id: i,
    src: `assets/img/remote-control/button${i}.png`,
    label: `Button ${i}`,
  }));

  private lcdObserver?: ResizeObserver;
  private statusObserver?: ResizeObserver;
  private displayLines: string[] = ['', '', '', ''];
  private subscriptions: Subscription[] = [];
  private currentState: RemoteControlState;

  constructor(
    private renderer: Renderer2,
    private marqueeService: MarqueeService,
    private queueService: QueueService,
    private keyMappingService: KeyMappingService
  ) {}

  ngOnInit() {
    this.marqueeService.init();

    this.subscriptions.push(
      this.queueService.state$.subscribe((state) => {
        this.currentState = state;
        this.marqueeService.updateStatusState(state);
      })
    );

    this.subscriptions.push(
      this.queueService.displayLines$.subscribe((lines) => {
        this.displayLines = lines;
      })
    );

    this.subscriptions.push(
      this.queueService.buttonQueue$.subscribe((queue) => {
        this.mobileButtonQueue = queue;
        this.buttonQueue.emit(queue);
      })
    );

    this.subscriptions.push(
      this.queueService.lastSendedButton$.subscribe((lastSendedButton) => {
        this.mobileLastSendedButton = lastSendedButton;
        this.lastSendedButton.emit(lastSendedButton);
      })
    );

    this.queueService.updateState({
      terminalConnected: this.terminal_connected,
    });

    if (this.terminal_connected) {
      this.queueService.startPolling(this.terminal_vid);
    } else {
      this.queueService.updateState({ terminalConnected: false });
    }
  }

  ngAfterViewInit() {
    const lcdConfig = { ratio: 0.038, min: 8, max: 13.8 };
    const statusConfig = { ratio: 0.04, min: 8, max: 13.8 };

    if (this.lcdText) {
      const container = this.lcdText?.nativeElement?.parentElement;
      if (!container) return;
      this.lcdObserver = this.setupObserver(
        container,
        this.lcdText.nativeElement,
        lcdConfig
      );
    }

    if (this.statusText) {
      const container = this.statusText?.nativeElement?.parentElement;
      if (!container) return;
      this.statusObserver = this.setupObserver(
        container,
        this.statusText.nativeElement,
        statusConfig
      );
    }
  }

  private setupObserver(
    container: HTMLElement,
    textEl: HTMLElement,
    cfg: { ratio: number; min: number; max: number }
  ): ResizeObserver | undefined {
    const update = () => {
      const w = container.getBoundingClientRect().width;
      const size = Math.max(cfg.min, Math.min(cfg.max, w * cfg.ratio));
      this.renderer.setStyle(textEl, 'font-size', `${size}px`);
    };

    update();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(update);
      ro.observe(container);
      return ro;
    } else {
      (window as Window & typeof globalThis).addEventListener('resize', update);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.queueService.stopPolling();
    this.marqueeService.cleanup();
    this.lcdObserver?.disconnect();
    this.statusObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.terminal_connected) {
      const connected = changes.terminal_connected.currentValue as boolean;

      if (connected) {
        this.queueService.updateState({ terminalConnected: connected });
        this.queueService.startPolling(this.terminal_vid);
      } else {
        this.queueService.stopPolling();
        this.queueService.updateState({
          terminalConnected: connected,
          sendingButton: false,
          readingScreen: false,
          ready: false,
        });
      }
    }
  }

  public get terminalDisplayLines(): string {
    return this.displayLines
      .map((line: string) => line.replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join('<br>');
  }

  public get isTerminalConnected(): boolean {
    return this.currentState?.terminalConnected;
  }

  public get isPending(): boolean {
    return this.currentState?.queueButtons > 0;
  }

  public get isReady(): boolean {
    return this.currentState?.ready;
  }

  public get computedMarqueeHTML(): SafeHtml {
    return this.marqueeService.computedMarqueeHTML;
  }

  public onButtonClick(button: RemoteButton): void {
    if (!this.terminal_connected) return;
    this.queueService.enqueueButton(button, this.terminal_vid);
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    this.keyMappingService.handleKeyPress(event, this.buttons, (btn) =>
      this.onButtonClick(btn)
    );
  }
}
